/**
 * Conteo de inventario guiado con varianza (C3 / RES-40).
 *
 * Lógica pura de la pantalla de conteo: qué insumos se cuentan, cómo se agrupan por categoría,
 * la diferencia contra el stock teórico, el borrador "guardar parcial y retomar" (con vencimiento
 * a los 7 días, igual que el borrador de ItemForm), el payload del POST y el CSV del detalle.
 *
 * Las entradas del usuario se guardan como texto tal cual se tipearon (`{ [itemId]: { counted,
 * reason } }`) para que el borrador devuelva exactamente lo que había en pantalla; se convierten
 * a número recién al calcular la diferencia o armar el payload.
 */

/** Tope de líneas por conteo que acepta el backend. */
export const MAX_LINES_PER_COUNT = 500;

/** Cuánto vive un borrador de conteo antes de descartarse (7 días). */
export const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/** Cantidad de insumos con mayor diferencia en $ que muestra el resumen. */
export const TOP_VARIANCES = 5;

const EPSILON = 1e-9;

const round = (value, decimals = 6) => {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
};

const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Clave del borrador en `storage` para un local.
 * @param {string} restaurantId - Local seleccionado
 * @returns {string} Clave
 */
export const draftStorageKey = (restaurantId) => `inventoryCountDraft_${restaurantId || 'default'}`;

/**
 * Un insumo entra al conteo si controla stock y está activo.
 * @param {Object} item - Fila de `getRestaurantStock()`
 * @returns {boolean} Si se cuenta
 */
export const isCountable = (item) =>
  Boolean(item && item.itemId) && item.trackStock !== false && item.isActive !== false;

/**
 * Agrupa los insumos contables por categoría. Las categorías van en orden alfabético y los
 * insumos sin categoría al final (category `''`); dentro de cada grupo, por nombre.
 * @param {Array<Object>} items - Filas de stock
 * @returns {Array<{category: string, items: Array<Object>}>} Grupos
 */
export function groupItemsByCategory(items) {
  const groups = new Map();
  for (const item of Array.isArray(items) ? items : []) {
    if (!isCountable(item)) continue;
    const category = (item.category || '').trim();
    if (!groups.has(category)) groups.set(category, []);
    groups.get(category).push(item);
  }
  const byName = (a, b) => (a.name || '').localeCompare(b.name || '', 'es');
  return [...groups.entries()]
    .sort(([a], [b]) => {
      if (!a) return 1;
      if (!b) return -1;
      return a.localeCompare(b, 'es');
    })
    .map(([category, list]) => ({ category, items: [...list].sort(byName) }));
}

/**
 * Interpreta lo tipeado en "conteo real". Acepta coma decimal.
 * @param {string|number|null|undefined} raw - Texto del input
 * @returns {{value: number|null, valid: boolean}} `value` null si está vacío; `valid` false si
 *   no es un número o es negativo
 */
export function parseCounted(raw) {
  if (raw === null || raw === undefined) return { value: null, valid: true };
  const text = String(raw).trim().replace(',', '.');
  if (text === '') return { value: null, valid: true };
  if (!/^\d*\.?\d+$|^\d+\.$/.test(text)) return { value: null, valid: false };
  const n = Number(text);
  if (!Number.isFinite(n) || n < 0) return { value: null, valid: false };
  return { value: n, valid: true };
}

/**
 * Diferencia contado − teórico (negativa = faltante, positiva = sobrante).
 * @param {number|null} theoretical - Stock teórico
 * @param {number|null} counted - Conteo real
 * @returns {number|null} Diferencia, o null si no se contó
 */
export function computeDifference(theoretical, counted) {
  const c = toNumber(counted);
  if (c === null) return null;
  return round(c - (toNumber(theoretical) || 0));
}

/**
 * Tipo de diferencia para colorearla.
 * @param {number|null} difference - Diferencia
 * @returns {'shortage'|'surplus'|'even'|null} Tipo
 */
export function differenceKind(difference) {
  if (difference === null || difference === undefined || Number.isNaN(difference)) return null;
  if (difference < -EPSILON) return 'shortage';
  if (difference > EPSILON) return 'surplus';
  return 'even';
}

/**
 * Valor en $ de una diferencia al costo promedio del insumo.
 * @param {number|null} difference - Diferencia en unidades
 * @param {number|null} unitCost - Costo unitario
 * @returns {number|null} Valor, o null si falta algún dato
 */
export function differenceValue(difference, unitCost) {
  const d = toNumber(difference);
  const c = toNumber(unitCost);
  if (d === null || c === null) return null;
  return round(d * c, 2);
}

/**
 * Formatea una cantidad (hasta 3 decimales, sin ceros de más).
 * @param {number|null} value - Cantidad
 * @param {{signed?: boolean}} [options] - `signed` antepone `+` a los positivos
 * @returns {string} Texto
 */
export function formatQuantity(value, { signed = false } = {}) {
  const n = toNumber(value);
  if (n === null) return '—';
  const rounded = round(n, 3);
  const text = String(Object.is(rounded, -0) ? 0 : rounded);
  return signed && rounded > 0 ? `+${text}` : text;
}

/**
 * Estado calculado de una línea a partir de lo tipeado.
 * @param {Object} item - Fila de stock
 * @param {{counted?: string, reason?: string}} [entry] - Entrada del usuario
 * @returns {{counted: number|null, valid: boolean, difference: number|null, kind: string|null, value: number|null}} Línea
 */
export function lineState(item, entry) {
  const { value, valid } = parseCounted(entry?.counted);
  const difference = valid ? computeDifference(item?.stock, value) : null;
  return {
    counted: value,
    valid,
    difference,
    kind: differenceKind(difference),
    value: differenceValue(difference, item?.avgCost),
  };
}

/**
 * Progreso de un grupo (o de toda la lista): cuántos insumos tienen un conteo válido.
 * @param {Array<Object>} items - Insumos
 * @param {Object} entries - Entradas por itemId
 * @returns {{counted: number, total: number}} Progreso
 */
export function countProgress(items, entries = {}) {
  const list = Array.isArray(items) ? items : [];
  const counted = list.filter((item) => {
    const { value, valid } = parseCounted(entries[item.itemId]?.counted);
    return valid && value !== null;
  }).length;
  return { counted, total: list.length };
}

/**
 * Insumos con un conteo tipeado que no es un número válido.
 * @param {Array<Object>} items - Insumos
 * @param {Object} entries - Entradas por itemId
 * @returns {Array<string>} itemIds con error
 */
export function invalidEntries(items, entries = {}) {
  return (Array.isArray(items) ? items : [])
    .filter((item) => !parseCounted(entries[item.itemId]?.counted).valid)
    .map((item) => item.itemId);
}

/**
 * Payload de `createInventoryCount`: solo las líneas contadas de insumos contables, en el orden
 * de `items`. El motivo se manda solo si tiene texto.
 * @param {Array<Object>} items - Insumos contables (en el orden de pantalla)
 * @param {Object} entries - Entradas por itemId
 * @param {string} [note] - Nota del conteo
 * @returns {{lines: Array<{itemId: string, counted: number, reason?: string}>, note?: string}} Payload
 */
export function buildCountPayload(items, entries = {}, note = '') {
  const lines = [];
  for (const item of Array.isArray(items) ? items : []) {
    if (!isCountable(item)) continue;
    const entry = entries[item.itemId];
    const { value, valid } = parseCounted(entry?.counted);
    if (!valid || value === null) continue;
    const line = { itemId: item.itemId, counted: value };
    const reason = (entry?.reason || '').trim();
    if (reason) line.reason = reason;
    lines.push(line);
  }
  const payload = { lines };
  const trimmedNote = (note || '').trim();
  if (trimmedNote) payload.note = trimmedNote;
  return payload;
}

/**
 * Cuántas líneas del payload tienen diferencia contra el teórico (para el modal de confirmación).
 * @param {Array<Object>} items - Insumos
 * @param {Array<{itemId: string, counted: number}>} lines - Líneas del payload
 * @returns {number} Líneas con diferencia
 */
export function linesWithDifference(items, lines) {
  const stockById = new Map((items || []).map((item) => [item.itemId, item.stock]));
  return (lines || []).filter(
    (line) => differenceKind(computeDifference(stockById.get(line.itemId), line.counted)) !== 'even'
  ).length;
}

/**
 * Líneas a reintentar de un conteo PARTIAL: las del payload enviado cuyo itemId falló.
 * @param {Array<Object>} sentLines - Líneas enviadas
 * @param {Array<string>} failedItemIds - itemIds que el backend no pudo ajustar
 * @returns {Array<Object>} Líneas a reenviar
 */
export function retryLines(sentLines, failedItemIds) {
  const failed = new Set(failedItemIds || []);
  return (sentLines || []).filter((line) => failed.has(line.itemId));
}

/**
 * Top de insumos con mayor diferencia en $ (en valor absoluto). Usa `summary.topVariances` si
 * viene del backend y, si no, lo calcula desde las líneas.
 * @param {Object} count - Respuesta del conteo
 * @param {number} [limit] - Cuántos
 * @returns {Array<{itemId: string, name: string, unit: string, difference: number, differenceValue: number}>} Top
 */
export function topVariances(count, limit = TOP_VARIANCES) {
  const fromSummary = count?.summary?.topVariances;
  const source =
    Array.isArray(fromSummary) && fromSummary.length > 0
      ? fromSummary
      : (count?.lines || []).filter((line) => differenceKind(line.difference) !== 'even');
  return [...source]
    .filter((line) => line && toNumber(line.difference) !== null)
    .sort((a, b) => Math.abs(b.differenceValue || 0) - Math.abs(a.differenceValue || 0))
    .slice(0, limit);
}

/**
 * Serializa el borrador para `storage`.
 * @param {{entries: Object, note?: string}} draft - Estado en curso
 * @param {number} [now] - Timestamp (inyectable para tests)
 * @returns {string} JSON
 */
export function serializeDraft({ entries, note } = {}, now = Date.now()) {
  const clean = {};
  Object.entries(entries || {}).forEach(([itemId, entry]) => {
    const counted = entry?.counted ?? '';
    const reason = entry?.reason ?? '';
    if (String(counted) !== '' || String(reason) !== '') clean[itemId] = { counted, reason };
  });
  return JSON.stringify({ entries: clean, note: note || '', savedAt: now });
}

/**
 * Lee el borrador guardado. Devuelve null si no hay, está corrupto o no tiene nada, y
 * `{ expired: true }` si venció (para que la página lo borre de `storage`).
 * @param {string|null} raw - Valor de `storage`
 * @param {number} [now] - Timestamp (inyectable para tests)
 * @returns {{entries: Object, note: string, savedAt: number, expired?: boolean}|null} Borrador
 */
export function parseDraft(raw, now = Date.now()) {
  if (!raw) return null;
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object' || !Number.isFinite(parsed.savedAt)) return null;
  if (!parsed.entries) return null;
  if (now - parsed.savedAt > DRAFT_MAX_AGE_MS) return { expired: true };
  const entries = parsed.entries && typeof parsed.entries === 'object' ? parsed.entries : {};
  if (Object.keys(entries).length === 0 && !parsed.note) return null;
  return { entries, note: parsed.note || '', savedAt: parsed.savedAt };
}

/** Escapa un valor para CSV (comillas dobles, comas, punto y coma y saltos de línea). */
const csvCell = (value) => {
  if (value === null || value === undefined) return '';
  const text = String(value);
  return /[",\n;]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/**
 * Filas del CSV del detalle de un conteo (sin la cabecera).
 * @param {Array<Object>} lines - Líneas del conteo
 * @returns {Array<Array<string|number>>} Filas
 */
export function countCsvRows(lines) {
  return (Array.isArray(lines) ? lines : []).map((line) => [
    line.name || line.itemId || '',
    line.category || '',
    line.unit || '',
    line.theoretical ?? '',
    line.counted ?? '',
    line.difference ?? '',
    line.unitCost ?? '',
    line.differenceValue ?? '',
    line.reason || '',
  ]);
}

/**
 * CSV del detalle de un conteo.
 * @param {Array<Object>} lines - Líneas del conteo
 * @param {Array<string>} headers - Cabeceras traducidas (insumo, categoría, unidad, teórico,
 *   contado, diferencia, costo unitario, diferencia $, motivo)
 * @returns {string} Contenido del CSV
 */
export function countLinesToCsv(lines, headers) {
  return [headers, ...countCsvRows(lines)].map((row) => row.map(csvCell).join(',')).join('\n');
}

/**
 * Nombre de archivo del export de un conteo.
 * @param {{countId?: string, createdAt?: string}} count - Conteo
 * @returns {string} Nombre de archivo
 */
export function countCsvFileName(count) {
  const date = (count?.createdAt || new Date().toISOString()).slice(0, 10);
  const id = (count?.countId || '').slice(0, 8);
  return `conteo_${date}${id ? `_${id}` : ''}.csv`;
}
